import { Button, generateResolver, Icon, Modal, Notification, STATUS_TYPES, VALIDATORS, yup } from 'dyl-components';
import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form';
import { Form, Popup } from 'semantic-ui-react';
import AddCampaignRegistrationForm from './AddCampaignRegistrationForm';
import { useDispatch, useSelector } from 'react-redux';
import tcrConfig from "actions/tcr"
import subscriptionActions from "actions/subscription";


const DEFAULT_VALUES = {
    campaign_name: "",
    main_usecase: "",
    other_usecase: [],
    description: "",
    website: "",
    terms_and_conditions_url: "",
    privacy_policy_url: "",
    sample_message_1: "",
    sample_message_2: "",
    sample_message_3: "",
    sample_message_4: "",
    sample_message_5: "",
    message_flow: "",
    required_and_optional_features: ["subscriber_opt_in", "subscriber_help"],
    approval_status: ""
};

export default function AddCampaignRegistrationModal({ open, onClose, type, campaign }) {
    const NAME_VALIDATOR = yup.string().no_whitespace_only().no_excessive_whitespaces().minlength(2).maxlength(64).simple_alphanumeric();
    const { brand, isReadingBrand } = useSelector((state) => state.tcr);
    const { high_volume_campaign_subscription_id, low_volume_campaign_subscription_id } = useSelector(state => state.subscription.tcr_details || {});

    const methods = useForm({
        defaultValues: DEFAULT_VALUES,
        resolver: generateResolver({
            "campaign_name": NAME_VALIDATOR.required('This field is required'),
            "main_usecase": yup.string().required('This field is required'),
            "other_usecase": yup.array()
                .of(yup.string().required('This field is required'))
                .when('main_usecase', {
                    is: value => value === 'MIXED',
                    then: schema => schema.min(2, "This should have at least two options"),
                })
                .when('business_type', {
                    is: value => value === 'PUBLIC_PROFIT',
                    then: schema => schema.min(1, "This should have at least one option"),
                    otherwise: schema => schema.nullable(true)
                }),
            "description": yup.string().minlength(40).maxlength(4096).no_whitespace_only().required('This field is required'),
            "website": VALIDATORS.WEBSITE().no_whitespace_only().maxlength(256).minlength(2).required('This field is required'),
            "terms_and_conditions_url": VALIDATORS.WEBSITE().no_whitespace_only().maxlength(256).minlength(2).required('This field is required'),
            "privacy_policy_url": VALIDATORS.WEBSITE().no_whitespace_only().maxlength(256).minlength(2).required('This field is required'),
            "sample_message_1": yup.string().required("This is a required field").minlength(20).maxlength(1024).no_excessive_whitespaces(),
            "sample_message_2": yup.string().required("This is a required field").minlength(20).maxlength(1024).no_excessive_whitespaces(),
            "sample_message_3": yup.string().required("This is a required field").minlength(20).maxlength(1024).no_excessive_whitespaces(),
            "sample_message_4": yup.string().required("This is a required field").minlength(20).maxlength(1024).no_excessive_whitespaces(),
            "sample_message_5": yup.string().required("This is a required field").minlength(20).maxlength(1024).no_excessive_whitespaces(),
            "message_flow": yup.string().minlength(40).required("This is a required field").maxlength(4096).no_excessive_whitespaces(),
            "required_and_optional_features": yup.array().of(yup.string().required('This field is required')).min(1, "This should have at least one option"),
        }),
        mode: "onChange",
    });

    const {
        handleSubmit,
        reset,
        watch,
        formState: { isValid, isDirty },
    } = methods;
    const dispatch = useDispatch();
    const { isCreatingBrandCampaign, isSavingBrandCampaign, isAssigningSlot } = useSelector((state) => state.tcr);

    const onSubmit = async (data, save_type = "submit") => {
        try {

            const payload = {
                // brandId: type !== "submit" ? brand?.brandId : campaign?.brandId,
                subUsecases: data.other_usecase,
                usecase: data.main_usecase,
                // resellerId : data.resellerId,
                description: data.description,
                termsAndConditions: data.terms_and_conditions_url ? true : false,
                termsAndConditionsLink: data.terms_and_conditions_url,
                privacyPolicyLink: data.privacy_policy_url,
                helpMessage: `${brand?.displayName} Support: Reply STOP to cancel. Msg frequency varies. Msg & data rates may apply. Contact us at ${brand?.email}.`,
                optoutMessage: `You have successfully unsubscribed from ${brand?.displayName} alerts. No further messages will be sent.`,
                optinMessage: `Welcome to ${brand?.displayName} alerts! Reply HELP for help, STOP to unsubscribe. Msg frequency varies. Msg & data rates may apply.`,
                optinKeywords: "start",
                sample1: data.sample_message_1,
                sample2: data.sample_message_2,
                sample3: data.sample_message_3,
                sample4: data.sample_message_4,
                sample5: data.sample_message_5,
                messageFlow: data.message_flow,
                tag: data.required_and_optional_features,
                // alternativeBusinessType : data.alternative_business_type,
                ageGated: data?.required_and_optional_features?.includes("age_gated"),
                embeddedLink: data.required_and_optional_features?.includes("embedded_link"),
                embeddedPhone: data.required_and_optional_features?.includes("embedded_phone"),
                subscriberOptIn: true,
                subscriberOptOut: true,
                directLending: data.required_and_optional_features?.includes("direct_lending"),
                numberPool: data.required_and_optional_features?.includes("number_pool"),
                embeddedLinkSample: data.website,
                name: data.campaign_name,

            }
            let campaign_id;
            if (save_type === "submit") {
                const response = await dispatch(tcrConfig.createBrandCampaign(payload, { campaign_id: campaign?.id }));
                campaign_id = response
            } else {
                const response = await dispatch(tcrConfig.saveBrandCampaign(payload, { campaign_id: campaign?.id }));
                campaign_id = response
            }
            if (!campaign?.id) {
                await dispatch(tcrConfig.assignTcrCampaignSlot(type === "high_volume" ? high_volume_campaign_subscription_id : low_volume_campaign_subscription_id, {
                    tcr_campaign_id: campaign_id
                }));
            }
            Notification.alert(`Campaign ${save_type === "save" ? "saved" : !campaign?.brandId ? "created" : "updated"} successfully`, STATUS_TYPES.SUCCESS);
            reset(DEFAULT_VALUES);
            onClose();
            await dispatch(tcrConfig.getBrandCampaigns());
            await dispatch(subscriptionActions.getTcrDetails());
        } catch (error) {
            console.error(error);
            Notification.alert(
                `Failed to ${save_type === "save" ? "save" : !campaign?.brandId ? "create" : "update"} campaign`,
                STATUS_TYPES.ERROR
            );
        }
    }
    const getWarningMessage = () => {
        if (brand?.status !== 'ACTIVE') {
            return 'Campaign creation is not allowed because brand registration is pending.';
        }
        if (brand?.entityType === 'PUBLIC_PROFIT') {
            return 'Campaign creation is not allowed for Public Profit brand.';
        }
    }
    useEffect(() => {
        if (campaign) {
            const features = ["subscriber_opt_in", "subscriber_help"];

            if (campaign?.ageGated) features.push("age_gated");
            if (campaign?.embeddedLink) features.push("embedded_link");
            if (campaign?.embeddedPhone) features.push("embedded_phone");
            if (campaign?.directLending) features.push("direct_lending");
            if (campaign?.numberPool) features.push("number_pool");


            const required_and_optional_features = features;

            methods.reset({
                campaign_name: campaign.name,
                main_usecase: campaign.usecase,
                other_usecase: campaign.subUsecases,
                description: campaign.description,
                website: campaign.embeddedLinkSample,
                terms_and_conditions_url: campaign.termsAndConditionsLink,
                privacy_policy_url: campaign.privacyPolicyLink,
                sample_message_1: campaign.sample1,
                sample_message_2: campaign.sample2,
                sample_message_3: campaign.sample3,
                sample_message_4: campaign.sample4,
                sample_message_5: campaign.sample5,
                message_flow: campaign.messageFlow,
                required_and_optional_features: required_and_optional_features,
                alternative_business_type: campaign.alternativeBusinessType,
                approval_status: campaign.status,
            });
        } else {
            methods.reset(type === "high_volume" ? DEFAULT_VALUES : { ...DEFAULT_VALUES, main_usecase: "LOW_VOLUME" });
        }
    }, [campaign, methods, type])
    return (
        <Modal open={open} onClose={() => {
            reset(DEFAULT_VALUES);
            onClose();
        }} size="large">
            <Modal.Header>{campaign?.id ? "Edit" : "Create"} TCR Campaign</Modal.Header>
            <Modal.Content scrolling>
                <Form loading={isCreatingBrandCampaign || isSavingBrandCampaign || isAssigningSlot}>
                    <FormProvider {...methods}>
                        <AddCampaignRegistrationForm type={type} campaign={campaign} />
                    </FormProvider>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                {(brand?.status !== 'ACTIVE' || brand?.entityType === 'PUBLIC_PROFIT') && !isReadingBrand && (
                    <Popup
                        trigger={<Icon size="large" className='fa-solid fa-triangle-exclamation WarningRed' />}
                        content={getWarningMessage()}
                        className='AddAccount__error'
                        position='left center'
                        positionFixed
                        wide
                        on="hover"
                    />
                )}
                <Button primary onClick={() => { onSubmit(watch(), "save") }} disabled={!isDirty || campaign?.campaignId || isSavingBrandCampaign || isAssigningSlot || isCreatingBrandCampaign} loading={isSavingBrandCampaign}>Save</Button>
                <Button primary onClick={() => { handleSubmit(onSubmit)() }} disabled={!isValid || brand?.status !== 'ACTIVE' || brand?.entityType === 'PUBLIC_PROFIT' || isCreatingBrandCampaign || isAssigningSlot || isSavingBrandCampaign} loading={isCreatingBrandCampaign}>Submit</Button>
            </Modal.Actions>
        </Modal>
    );
};
